export type UserLocation = { lat: number; lon: number } | null

type GpsError =
  | 'NOT_SUPPORTED'
  | 'INSECURE_CONTEXT'
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'UNKNOWN'

type GpsAttempt =
  | { location: { lat: number; lon: number }; error: null }
  | { location: null; error: GpsError }

function requestPosition(options: PositionOptions): Promise<GpsAttempt> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        resolve({ location: { lat: latitude, lon: longitude }, error: null })
      },
      (err) => {
        const code = typeof err?.code === 'number' ? err.code : undefined
        const mapped: GpsError =
          code === 1
            ? 'PERMISSION_DENIED'
            : code === 2
              ? 'POSITION_UNAVAILABLE'
              : code === 3
                ? 'TIMEOUT'
                : 'UNKNOWN'
        resolve({ location: null, error: mapped })
      },
      options
    )
  })
}

async function getGpsLocation(): Promise<GpsAttempt> {
  if (!('geolocation' in navigator)) return { location: null, error: 'NOT_SUPPORTED' }
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return { location: null, error: 'INSECURE_CONTEXT' }
  }

  // ---- Step 1: ensure permission is granted BEFORE starting a timed request ----
  // Some browsers (Chrome, Edge) count the permission-dialog wait against the
  // getCurrentPosition timeout, so a 6-10 s timeout can expire while the user
  // is still reading the prompt.  We separate the two phases:
  //   a) trigger the dialog and wait for the user's decision
  //   b) request coordinates with a timeout that only covers the actual fix
  try {
    const perm = await navigator.permissions?.query({ name: 'geolocation' as PermissionName })
    if (perm?.state === 'denied') return { location: null, error: 'PERMISSION_DENIED' }

    if (perm?.state === 'prompt') {
      // Permission not yet decided — trigger the browser dialog and wait
      // for the user to accept or deny (no tight timeout here).
      const dialogResult = await new Promise<GpsAttempt>((resolve) => {
        let settled = false
        const settle = (v: GpsAttempt) => { if (!settled) { settled = true; resolve(v) } }

        // Listen for permission state change (fires immediately on user click)
        const onChange = () => {
          perm.removeEventListener('change', onChange)
          if (perm.state === 'denied') settle({ location: null, error: 'PERMISSION_DENIED' })
          // If granted, don't resolve yet — let the getCurrentPosition callback do it
          // so we capture the position from this very request.
        }
        perm.addEventListener('change', onChange)

        // Fire getCurrentPosition with a generous timeout (60s) just to
        // trigger the dialog. If it returns a position, great — we use it.
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            perm.removeEventListener('change', onChange)
            settle({ location: { lat: pos.coords.latitude, lon: pos.coords.longitude }, error: null })
          },
          (err) => {
            perm.removeEventListener('change', onChange)
            const code = typeof err?.code === 'number' ? err.code : undefined
            const mapped: GpsError =
              code === 1 ? 'PERMISSION_DENIED'
              : code === 2 ? 'POSITION_UNAVAILABLE'
              : code === 3 ? 'TIMEOUT'
              : 'UNKNOWN'
            settle({ location: null, error: mapped })
          },
          { enableHighAccuracy: false, timeout: 30000, maximumAge: 10 * 60 * 1000 }
        )

        // Safety: if nothing fires within 35 s, give up
        setTimeout(() => settle({ location: null, error: 'TIMEOUT' }), 35000)
      })

      // If the dialog request already got a position, return it directly
      if (dialogResult.location) return dialogResult
      // If the user denied, no point retrying
      if (dialogResult.error === 'PERMISSION_DENIED') return dialogResult
      // For other errors (TIMEOUT / POSITION_UNAVAILABLE), fall through to
      // the high-accuracy retry below.
    }
  } catch {
    // permissions API not available — proceed directly
    void 0
  }

  // ---- Step 2: permission is granted (or we couldn't check), get coordinates ----
  const quick = await requestPosition({ enableHighAccuracy: false, timeout: 12000, maximumAge: 10 * 60 * 1000 })
  if (quick.location) return quick
  if (quick.error === 'PERMISSION_DENIED' || quick.error === 'INSECURE_CONTEXT' || quick.error === 'NOT_SUPPORTED') return quick

  // Last resort: high-accuracy (GPS chip if present)
  const precise = await requestPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 })
  return precise
}

async function getIpLocation(): Promise<UserLocation> {
  try {
    const r = await fetch('https://ipapi.co/json/')
    if (!r.ok) return null
    const j = await r.json()
    const lat = typeof j.latitude === 'number' ? j.latitude : parseFloat(j.latitude)
    const lon = typeof j.longitude === 'number' ? j.longitude : parseFloat(j.longitude)
    if (isNaN(lat) || isNaN(lon)) return null
    return { lat, lon }
  } catch {
    return null
  }
}

export type UserContext =
  | { lat: number; lon: number; source: 'GPS'; timezoneOffset: number }
  | { lat: number; lon: number; source: 'IP'; timezoneOffset: number; gpsError?: GpsError }
  | null

export async function detectUserContext(): Promise<UserContext> {
  const timezoneOffset = -new Date().getTimezoneOffset() / 60
  const gps = await getGpsLocation()
  if (gps.location) return { lat: gps.location.lat, lon: gps.location.lon, source: 'GPS', timezoneOffset }
  const ip = await getIpLocation()
  if (ip) return { lat: ip.lat, lon: ip.lon, source: 'IP', timezoneOffset, gpsError: gps.error }
  return null
}
